import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { format } from "date-fns";

// Start Work Step Form Component
export function StartWorkStepForm({ assignment, defaultStartTime, onStart, onCancel }) {
  const [currentStartTime, setCurrentStartTime] = useState(defaultStartTime);
  
  useEffect(() => {
    setCurrentStartTime(format(new Date(), "HH:mm"));
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const startTimeStr = formData.get('start_time');
    const [hours, minutes] = startTimeStr.split(':');
    const startTime = new Date();
    startTime.setHours(parseInt(hours), parseInt(minutes), 0);
    
    const startData = {
      start_time: startTime.toISOString(),
      work_notes: formData.get('work_notes')
    };
    
    onStart(startData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-blue-50 p-3 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Arbeitsschritt-Informationen</h4>
        <p className="text-sm text-blue-700">
          <strong>Aufgabe:</strong> {assignment.task?.title}
        </p>
        <p className="text-sm text-blue-700">
          <strong>Arbeitsschritt:</strong> {assignment.work_step?.step_type}
        </p>
        {assignment.work_step?.notes && (
          <p className="text-sm text-blue-700 mt-1">
            <strong>Hinweise:</strong> {assignment.work_step.notes}
          </p>
        )}
      </div>
      
      <div>
        <Label htmlFor="start_time">Startzeit</Label>
        <Input
          id="start_time"
          name="start_time"
          type="time"
          value={currentStartTime}
          onChange={(e) => setCurrentStartTime(e.target.value)}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="work_notes">Arbeitsnotizen (optional)</Label>
        <Textarea
          id="work_notes"
          name="work_notes"
          placeholder="Notizen zum Start der Arbeit..."
          rows={2}
        />
      </div>
      
      <div className="flex space-x-2">
        <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
          Arbeitsschritt starten
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

// Pause/Complete Work Step Form Component  
export function PauseCompleteWorkStepForm({ assignment, timeEntries, currentSessionStart, totalMinutes, onPause, onComplete, onCancel }) {
  const [action, setAction] = useState('pause');
  const [currentEndTime, setCurrentEndTime] = useState(format(new Date(), "HH:mm"));
  
  useEffect(() => {
    setCurrentEndTime(format(new Date(), "HH:mm"));
  }, [action]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const endTimeStr = formData.get('end_time');
    const [hours, minutes] = endTimeStr.split(':');
    const endTime = new Date();
    endTime.setHours(parseInt(hours), parseInt(minutes), 0);
    
    const data = {
      end_time: endTime.toISOString(),
      actual_quantity: formData.get('actual_quantity') ? parseInt(formData.get('actual_quantity')) : null,
      work_notes: formData.get('work_notes')
    };
    
    if (action === 'pause') {
      onPause(data);
    } else {
      data.completion_notes = formData.get('completion_notes');
      onComplete(data);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-yellow-50 p-3 rounded-lg">
        <h4 className="font-medium text-yellow-900 mb-2">Aktuelle Sitzung</h4>
        <p className="text-sm text-yellow-700">
          <strong>Gestartet um:</strong> {currentSessionStart || 'Unbekannt'}
        </p>
        <p className="text-sm text-yellow-700">
          <strong>Bisherige Gesamtzeit:</strong> {totalMinutes} Minuten
        </p>
        <p className="text-sm text-yellow-700">
          <strong>Anzahl Sitzungen:</strong> {timeEntries.length}
        </p>
      </div>
      
      <div className="flex space-x-2 mb-4">
        <Button 
          type="button"
          variant={action === 'pause' ? 'default' : 'outline'}
          onClick={() => setAction('pause')}
          className="flex-1"
        >
          Pausieren
        </Button>
        <Button 
          type="button"
          variant={action === 'complete' ? 'default' : 'outline'}
          onClick={() => setAction('complete')}
          className="flex-1"
        >
          Abschließen
        </Button>
      </div>
      
      <div>
        <Label htmlFor="end_time">Endzeit</Label>
        <Input
          id="end_time"
          name="end_time"
          type="time"
          value={currentEndTime}
          onChange={(e) => setCurrentEndTime(e.target.value)}
          required
        />
      </div>
      
      <div>
        <Label htmlFor="actual_quantity">Ist-Stückzahl (optional)</Label>
        <Input
          id="actual_quantity"
          name="actual_quantity"
          type="number"
          min="0"
          placeholder="Anzahl bearbeiteter Stücke in dieser Sitzung"
        />
      </div>
      
      <div>
        <Label htmlFor="work_notes">Arbeitsnotizen</Label>
        <Textarea
          id="work_notes"
          name="work_notes"
          placeholder="Was wurde in dieser Sitzung gemacht..."
          rows={2}
        />
      </div>
      
      {action === 'complete' && (
        <div>
          <Label htmlFor="completion_notes">Abschluss-Notizen</Label>
          <Textarea
            id="completion_notes"
            name="completion_notes"
            placeholder="Finale Notizen zum abgeschlossenen Arbeitsschritt..."
            rows={2}
          />
        </div>
      )}
      
      <div className="flex space-x-2">
        <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
          {action === 'pause' ? 'Pausieren' : 'Abschließen'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

// Resume/Complete Work Step Form Component
export function ResumeCompleteWorkStepForm({ assignment, timeEntries, totalMinutes, currentStartTime, setCurrentStartTime, onStart, onComplete, onCancel }) {
  const [action, setAction] = useState('resume');
  
  useEffect(() => {
    if (action === 'resume') {
      setCurrentStartTime(format(new Date(), "HH:mm"));
    }
  }, [action, setCurrentStartTime]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    if (action === 'resume') {
      const startTimeStr = formData.get('start_time');
      const [hours, minutes] = startTimeStr.split(':');
      const startTime = new Date();
      startTime.setHours(parseInt(hours), parseInt(minutes), 0);
      
      const startData = {
        start_time: startTime.toISOString(),
        work_notes: formData.get('work_notes')
      };
      
      onStart(startData);
    } else {
      const completionData = {
        completion_notes: formData.get('completion_notes')
      };
      
      onComplete(completionData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-green-50 p-3 rounded-lg">
        <h4 className="font-medium text-green-900 mb-2">Pausierter Arbeitsschritt</h4>
        <p className="text-sm text-green-700">
          <strong>Bisherige Gesamtzeit:</strong> {totalMinutes} Minuten
        </p>
        <p className="text-sm text-green-700">
          <strong>Anzahl Sitzungen:</strong> {timeEntries.length}
        </p>
      </div>
      
      <div className="flex space-x-2 mb-4">
        <Button 
          type="button"
          variant={action === 'resume' ? 'default' : 'outline'}
          onClick={() => setAction('resume')}
          className="flex-1"
        >
          Fortsetzen
        </Button>
        <Button 
          type="button"
          variant={action === 'complete' ? 'default' : 'outline'}
          onClick={() => setAction('complete')}
          className="flex-1"
        >
          Ohne Fortsetzen abschließen
        </Button>
      </div>
      
      {action === 'resume' && (
        <>
          <div>
            <Label htmlFor="start_time">Startzeit für Fortsetzung</Label>
            <Input
              id="start_time"
              name="start_time"
              type="time"
              value={currentStartTime}
              onChange={(e) => setCurrentStartTime(e.target.value)}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="work_notes">Arbeitsnotizen (optional)</Label>
            <Textarea
              id="work_notes"
              name="work_notes"
              placeholder="Notizen zur Fortsetzung der Arbeit..."
              rows={2}
            />
          </div>
        </>
      )}
      
      {action === 'complete' && (
        <div>
          <Label htmlFor="completion_notes">Abschluss-Notizen</Label>
          <Textarea
            id="completion_notes"
            name="completion_notes"
            placeholder="Finale Notizen zum abgeschlossenen Arbeitsschritt..."
            rows={3}
          />
        </div>
      )}
      
      <div className="flex space-x-2">
        <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
          {action === 'resume' ? 'Fortsetzen' : 'Abschließen'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Abbrechen
        </Button>
      </div>
    </form>
  );
}
